import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, IconButton, List, ListItem } from '@mui/material';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  MenuItemLink,
  MenuItemLinkClasses,
  useCanAccess,
  useResourceDefinitions,
  useSidebarState,
} from 'react-admin';
import { useLocation } from 'react-router-dom';

export interface SubMenuLink {
  key: string;
  label: string;
  to: string;
  visible: boolean;
}

interface CollapsibleResourceMenuItemProps {
  resource: string;
  icon: ReactNode;
  items: SubMenuLink[];
}

function getResourceLabel(
  resource: string,
  resourceDefinitions: ReturnType<typeof useResourceDefinitions>,
): string {
  const label = resourceDefinitions[resource]?.options?.label;
  if (typeof label === 'string' && label.length > 0) {
    return label;
  }

  return resource
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function CollapsibleResourceMenuItem({
  resource,
  icon,
  items,
}: CollapsibleResourceMenuItemProps): ReactElement | null {
  const [sidebarIsOpen] = useSidebarState();
  const resourceDefinitions = useResourceDefinitions();
  const resourceAccess = useCanAccess({ resource, action: 'list' });
  const location = useLocation();
  const visibleItems = useMemo(() => items.filter((item) => item.visible), [items]);
  const childRouteIsActive = visibleItems.some((item) => location.pathname === item.to);
  const [isOpen, setIsOpen] = useState(childRouteIsActive);

  useEffect(() => {
    if (childRouteIsActive) {
      setIsOpen(true);
    }
  }, [childRouteIsActive]);

  if (!resourceAccess.canAccess) {
    return null;
  }

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MenuItemLink
            className={childRouteIsActive ? MenuItemLinkClasses.active : undefined}
            leftIcon={icon}
            primaryText={getResourceLabel(resource, resourceDefinitions)}
            sidebarIsOpen={sidebarIsOpen}
            to={`/${resource}`}
          />
        </Box>
        {sidebarIsOpen && visibleItems.length > 0 && (
          <IconButton
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${resource}`}
            onClick={() => setIsOpen((open) => !open)}
            size="small"
            sx={{
              alignSelf: 'center',
              borderRadius: 1,
              mr: sidebarIsOpen ? 1 : 0.5,
            }}
          >
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>
      {visibleItems.length > 0 && (
        <Collapse in={sidebarIsOpen && isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {visibleItems.map((item) => (
              <MenuItemLink
                key={item.key}
                primaryText={item.label}
                sidebarIsOpen={sidebarIsOpen}
                sx={{ pl: 7 }}
                to={item.to}
              />
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
}
